import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tracks unsaved local drafts and refuses to let them vanish silently.
 *
 * The prototype discarded parameter edits and whole condition trees on close or reload without a
 * word. Two guards here: a beforeunload handler for closing or reloading the tab, and a
 * confirm() gate that in-app navigation goes through.
 *
 * Drafts are registered by key (a rule id, "definition", "linking-rule") so the count reported
 * to the user is the number of things they would lose, not a boolean.
 */
export function useUnsavedChanges() {
    const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(() => new Set());

    // Kept in a ref as well so the beforeunload handler does not need re-registering on every
    // keystroke, and so it always reads the current set rather than a captured one.
    const dirtyRef = useRef(dirtyKeys);
    dirtyRef.current = dirtyKeys;

    const markDirty = useCallback((key: string) => {
        setDirtyKeys((current) => {
            if (current.has(key)) {
                return current;
            }
            const next = new Set(current);
            next.add(key);
            return next;
        });
    }, []);

    const markClean = useCallback((key: string) => {
        setDirtyKeys((current) => {
            if (!current.has(key)) {
                return current;
            }
            const next = new Set(current);
            next.delete(key);
            return next;
        });
    }, []);

    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            if (dirtyRef.current.size === 0) {
                return;
            }
            // Browsers show their own wording; setting returnValue is what triggers the prompt.
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, []);

    /**
     * Gate an action that would discard drafts. Returns false when the user backs out.
     *
     * Uses confirm() deliberately: it is synchronous, so a caller can guard a navigation without
     * restructuring into a promise, and it cannot be missed the way an in-page banner can.
     */
    const confirmDiscard = useCallback((what: string) => {
        const count = dirtyRef.current.size;
        if (count === 0) {
            return true;
        }
        return window.confirm(
            `${what} will discard ${count} unsaved ${
                count === 1 ? "change" : "changes"
            }. Continue?`,
        );
    }, []);

    return {
        isDirty: dirtyKeys.size > 0,
        dirtyCount: dirtyKeys.size,
        markDirty,
        markClean,
        confirmDiscard,
    };
}
